(function () {
  'use strict';

  function parsePx(styleVal, fallback) {
    var n = parseFloat(styleVal);
    return Number.isFinite(n) ? n : fallback;
  }

  function clamp(n, min, max) {
    return Math.min(Math.max(n, min), max);
  }

  function readViewportSize() {
    var vv = window.visualViewport;
    if (vv && vv.width > 0 && vv.height > 0) {
      return { vw: vv.width, vh: vv.height };
    }
    return { vw: window.innerWidth, vh: window.innerHeight };
  }

  function getFocusable(container) {
    if (!container) return [];
    var sel =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, video[controls], [tabindex]:not([tabindex="-1"])';
    return Array.prototype.slice.call(container.querySelectorAll(sel)).filter(function (el) {
      if (el.closest('[aria-hidden="true"]')) return false;
      return el.getClientRects().length > 0;
    });
  }

  class CVideoPopup extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._hydrateFromTemplate();

      this._shell = this.querySelector('.js-cvp-shell');
      this._btnCloseMini = this.querySelector('.c-video-popup__btn--close-mini');
      this._btnCloseFs = this.querySelector('.c-video-popup__btn--close-fs');
      this._btnPlay = this.querySelector('.js-cvp-play');
      this._posterEl = this.querySelector('.js-cvp-poster');
      this._mode = this.getAttribute('data-mode') || 'none';
      this._youtubeId = this.getAttribute('data-youtube-id') || '';
      this._vimeoId = this.getAttribute('data-vimeo-id') || '';
      this._dialogLabel = this.getAttribute('data-dialog-label') || 'Video';

      this._previewStartSec = parseFloat(this.getAttribute('data-preview-start-sec') || '3');
      if (!Number.isFinite(this._previewStartSec) || this._previewStartSec < 0) this._previewStartSec = 3;
      this._previewLoopSec = parseFloat(this.getAttribute('data-preview-loop-sec') || '3');
      if (!Number.isFinite(this._previewLoopSec) || this._previewLoopSec <= 0) this._previewLoopSec = 3;

      this._previewYtTimer = null;
      this._previewVimeoTimer = null;
      this._shopifyLoopLo = 0;
      this._shopifyLoopHi = 0;
      this._shopifyMetaHandled = false;
      this._onShopifyTimeUpdate = this._onShopifyTimeUpdate.bind(this);
      this._onShopifyLoadedMeta = this._onShopifyLoadedMeta.bind(this);

      this._videoEl = this.querySelector('video.js-cvp-html5');
      this._ytIframe = this.querySelector('iframe.js-cvp-youtube');
      this._vimeoIframe = this.querySelector('iframe.js-cvp-vimeo');

      this._dismissedBeforeFs = false;
      this._prevFocus = null;
      this._dragging = false;
      this._dragStartX = 0;
      this._dragStartY = 0;
      this._dragShellOrigLeft = 0;
      this._dragShellOrigBottom = 0;

      this._onDocKeydown = this._onDocKeydown.bind(this);
      this._onShellKeydown = this._onShellKeydown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);

      this._bindIframeTabOrder();

      if (this._posterEl) {
        this._posterEl.setAttribute('aria-hidden', 'false');
      }
      if (this._btnPlay) {
        this._btnPlay.setAttribute('type', 'button');
      }

      this._ariaShellIdle();

      if (this._btnCloseMini) {
        this._btnCloseMini.addEventListener('click', function (e) {
          e.stopPropagation();
          this._dismissCorner();
        }.bind(this));
      }

      if (this._btnCloseFs) {
        this._btnCloseFs.addEventListener('click', function (e) {
          e.stopPropagation();
          this._exitFullscreen();
        }.bind(this));
      }

      if (this._btnPlay) {
        this._btnPlay.addEventListener('click', function (e) {
          e.stopPropagation();
          this._enterFullscreen();
        }.bind(this));
      }

      if (this._shell) {
        this._setupDrag(this._shell);
      }

      document.addEventListener('keydown', this._onDocKeydown);

      this.removeAttribute('hidden');
      this.removeAttribute('aria-hidden');
      this.classList.add('is-ready');

      var self = this;
      requestAnimationFrame(function () {
        self._bootReveal();
      });
    }

    _hydrateFromTemplate() {
      var tpl = this.querySelector(':scope > template.js-cvp-tpl');
      if (!tpl || !tpl.content) return;
      this.appendChild(tpl.content.cloneNode(true));
      tpl.remove();
    }

    disconnectedCallback() {
      this._clearDismissAnimation();
      this._clearBootAndPreviewTimers();
      if (this._videoEl) {
        this._videoEl.removeEventListener('timeupdate', this._onShopifyTimeUpdate);
        this._videoEl.removeEventListener('loadedmetadata', this._onShopifyLoadedMeta);
      }
      this._shopifyMetaHandled = false;
      document.removeEventListener('keydown', this._onDocKeydown);
      if (this._shell) this._shell.removeEventListener('keydown', this._onShellKeydown);
      window.removeEventListener('mousemove', this._onPointerMove);
      window.removeEventListener('mouseup', this._onPointerUp);
      window.removeEventListener('touchmove', this._onPointerMove);
      window.removeEventListener('touchend', this._onPointerUp);
      this._initialized = false;
    }

    _clearBootAndPreviewTimers() {
      if (this._previewYtTimer) {
        clearInterval(this._previewYtTimer);
        this._previewYtTimer = null;
      }
      if (this._previewVimeoTimer) {
        clearInterval(this._previewVimeoTimer);
        this._previewVimeoTimer = null;
      }
    }

    _bootReveal() {
      if (this._mode === 'shopify' && this._videoEl) {
        try {
          this._videoEl.preload = 'auto';
        } catch (_) {}
        this._videoEl.addEventListener('loadedmetadata', this._onShopifyLoadedMeta, { once: true });
        try {
          this._videoEl.load();
        } catch (_) {}
        if (this._videoEl.readyState >= 1) {
          this._onShopifyLoadedMeta();
        }
      } else if (this._mode === 'youtube' && this._ytIframe && this._youtubeId) {
        this._loadIframePreviewYoutube();
      } else if (this._mode === 'vimeo' && this._vimeoIframe && this._vimeoId) {
        this._loadIframePreviewVimeo();
      }

      var self = this;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          self._clampShellIntoViewport();
        });
      });
    }

    _computeShopifyLoopRange() {
      var start = this._previewStartSec;
      var len = this._previewLoopSec;
      var d = this._videoEl && this._videoEl.duration;
      if (!Number.isFinite(d) || d <= 0) {
        this._shopifyLoopLo = start;
        this._shopifyLoopHi = start + len;
        return;
      }
      if (d <= start) {
        this._shopifyLoopLo = 0;
        this._shopifyLoopHi = d;
        return;
      }
      var hi = Math.min(start + len, d);
      this._shopifyLoopLo = start;
      this._shopifyLoopHi = hi;
    }

    _onShopifyLoadedMeta() {
      if (!this._videoEl || this._shopifyMetaHandled) return;
      this._shopifyMetaHandled = true;
      this._computeShopifyLoopRange();
      this._videoEl.muted = true;
      this._videoEl.removeEventListener('timeupdate', this._onShopifyTimeUpdate);
      this._videoEl.addEventListener('timeupdate', this._onShopifyTimeUpdate);
      try {
        this._videoEl.currentTime = this._shopifyLoopLo;
      } catch (_) {}
      var p = this._videoEl.play();
      if (p && typeof p.then === 'function') p.catch(function () {});
    }

    _onShopifyTimeUpdate() {
      if (!this._videoEl) return;
      if (
        this.classList.contains('is-fullscreen') ||
        this.classList.contains('is-dismissed') ||
        this.classList.contains('is-dismissing')
      )
        return;
      var v = this._videoEl;
      var lo = this._shopifyLoopLo;
      var hi = this._shopifyLoopHi;
      if (!(hi > lo)) return;
      if (v.currentTime >= hi - 0.04) {
        try {
          v.currentTime = lo;
        } catch (_) {}
      }
    }

    _ensureYoutubePreviewLoopTimer() {
      if (this._previewYtTimer || !this._ytIframe || !this._youtubeId) return;
      var self = this;
      var ms = this._previewLoopSec * 1000;
      var t = Math.floor(this._previewStartSec);
      this._previewYtTimer = setInterval(function () {
        if (!self._ytIframe || !self._ytIframe.contentWindow) return;
        if (
          self.classList.contains('is-fullscreen') ||
          self.classList.contains('is-dismissed') ||
          self.classList.contains('is-dismissing')
        )
          return;
        try {
          self._ytIframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'seekTo', args: [t, true] }),
            '*'
          );
          self._ytIframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
            '*'
          );
        } catch (_) {}
      }, ms);
    }

    _loadIframePreviewYoutube() {
      if (!this._ytIframe || !this._youtubeId) return;
      this._stopIframePreviewTimers();
      var start = Math.floor(this._previewStartSec);
      var q =
        'enablejsapi=1&rel=0&playsinline=1&mute=1&autoplay=1&controls=0&modestbranding=1&start=' +
        encodeURIComponent(String(start));
      this._ytIframe.src =
        'https://www.youtube.com/embed/' + encodeURIComponent(this._youtubeId) + '?' + q;
      this._ytIframe.setAttribute('tabindex', '-1');
      this._ensureYoutubePreviewLoopTimer();
    }

    _ensureVimeoPreviewLoopTimer() {
      if (this._previewVimeoTimer || !this._vimeoIframe || !this._vimeoId) return;
      var self = this;
      var ms = this._previewLoopSec * 1000;
      var start = this._previewStartSec;
      this._previewVimeoTimer = setInterval(function () {
        if (!self._vimeoIframe || !self._vimeoIframe.contentWindow) return;
        if (
          self.classList.contains('is-fullscreen') ||
          self.classList.contains('is-dismissed') ||
          self.classList.contains('is-dismissing')
        )
          return;
        try {
          self._vimeoIframe.contentWindow.postMessage(
            JSON.stringify({ method: 'setCurrentTime', value: start }),
            '*'
          );
          self._vimeoIframe.contentWindow.postMessage(JSON.stringify({ method: 'play' }), '*');
        } catch (_) {}
      }, ms);
    }

    _loadIframePreviewVimeo() {
      if (!this._vimeoIframe || !this._vimeoId) return;
      this._stopIframePreviewTimers();
      var start = this._previewStartSec;
      var base =
        'https://player.vimeo.com/video/' +
        encodeURIComponent(this._vimeoId) +
        '?autoplay=1&muted=1&playsinline=1&background=1&api=1&controls=0';
      this._vimeoIframe.src = base + '#t=' + String(start) + 's';
      this._vimeoIframe.setAttribute('tabindex', '-1');
      this._ensureVimeoPreviewLoopTimer();
    }

    _stopIframePreviewTimers() {
      if (this._previewYtTimer) {
        clearInterval(this._previewYtTimer);
        this._previewYtTimer = null;
      }
      if (this._previewVimeoTimer) {
        clearInterval(this._previewVimeoTimer);
        this._previewVimeoTimer = null;
      }
    }

    _bindIframeTabOrder() {
      if (this._mode === 'youtube' && this._ytIframe) this._ytIframe.setAttribute('tabindex', '-1');
      if (this._mode === 'vimeo' && this._vimeoIframe)
        this._vimeoIframe.setAttribute('tabindex', '-1');
      if (this._mode === 'shopify' && this._videoEl) this._videoEl.setAttribute('tabindex', '-1');
    }

    _ariaShellIdle() {
      if (!this._shell) return;
      this._shell.setAttribute('role', 'region');
      this._shell.setAttribute('aria-label', this._dialogLabel);
      this._shell.removeAttribute('aria-modal');
    }

    _ariaShellFullscreen() {
      if (!this._shell) return;
      this._shell.setAttribute('role', 'dialog');
      this._shell.setAttribute('aria-modal', 'true');
      this._shell.setAttribute('aria-label', this._dialogLabel);
    }

    _revealPoster(show) {
      if (!this._posterEl) return;
      this._posterEl.classList.toggle('is-hidden', !show);
      this._posterEl.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    _revealPlay(show) {
      if (!this._btnPlay) return;
      this._btnPlay.classList.toggle('is-hidden', !show);
      if (show) this._btnPlay.removeAttribute('aria-hidden');
      else this._btnPlay.setAttribute('aria-hidden', 'true');
    }

    _pauseAll() {
      if (this._videoEl) {
        try {
          this._videoEl.pause();
        } catch (_) {}
      }
      if (this._ytIframe && this._ytIframe.contentWindow) {
        try {
          this._ytIframe.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
            '*'
          );
        } catch (_) {}
      }
      if (this._vimeoIframe && this._vimeoIframe.contentWindow) {
        try {
          this._vimeoIframe.contentWindow.postMessage('{"method":"pause"}', '*');
        } catch (_) {}
      }
    }

    _loadIframePlay(autoplay) {
      if (this._mode === 'youtube' && this._ytIframe && this._youtubeId) {
        var q = 'enablejsapi=1&rel=0&playsinline=1';
        q += autoplay ? '&autoplay=1' : '&autoplay=0';
        this._ytIframe.src =
          'https://www.youtube.com/embed/' + encodeURIComponent(this._youtubeId) + '?' + q;
        this._ytIframe.removeAttribute('tabindex');
      }
      if (this._mode === 'vimeo' && this._vimeoIframe && this._vimeoId) {
        var vq = 'autoplay=' + (autoplay ? '1' : '0') + '&playsinline=1';
        this._vimeoIframe.src =
          'https://player.vimeo.com/video/' + encodeURIComponent(this._vimeoId) + '?' + vq;
        this._vimeoIframe.removeAttribute('tabindex');
      }
    }

    _enterFullscreen() {
      this._dismissedBeforeFs = this.classList.contains('is-dismissed');
      this._prevFocus = document.activeElement;
      this.classList.add('is-fullscreen');
      this._setShellDismissedA11y(false);
      this._ariaShellFullscreen();
      if (this._shell) this._shell.addEventListener('keydown', this._onShellKeydown);

      this._revealPoster(false);
      this._revealPlay(false);

      this._stopIframePreviewTimers();

      if (this._mode === 'shopify' && this._videoEl) {
        var v = this._videoEl;
        v.controls = true;
        v.removeAttribute('tabindex');
        try {
          v.preload = 'auto';
        } catch (_) {}
        try {
          v.muted = false;
          v.currentTime = 0;
        } catch (_) {}
        var p = v.play();
        if (p && typeof p.then === 'function') {
          p.catch(function () {
            function onCanPlay() {
              v.removeEventListener('canplay', onCanPlay);
              var p2 = v.play();
              if (p2 && typeof p2.then === 'function') p2.catch(function () {});
            }
            v.addEventListener('canplay', onCanPlay, { once: true });
          });
        }
      } else {
        this._loadIframePlay(true);
      }

      requestAnimationFrame(
        function () {
          if (this._btnCloseFs) this._btnCloseFs.focus();
        }.bind(this)
      );
    }

    _exitFullscreen() {
      this.classList.remove('is-fullscreen');
      if (this._shell) this._shell.removeEventListener('keydown', this._onShellKeydown);
      this._ariaShellIdle();
      this._pauseAll();

      if (this._mode === 'shopify' && this._videoEl) {
        this._videoEl.controls = false;
        this._videoEl.setAttribute('tabindex', '-1');
      }

      this._bindIframeTabOrder();

      if (this._dismissedBeforeFs) {
        this.classList.add('is-dismissed');
        this._setShellDismissedA11y(true);
      } else {
        this.classList.remove('is-dismissed');
        this._setShellDismissedA11y(false);
      }

      this._revealPoster(true);
      this._revealPlay(true);

      if (!this.classList.contains('is-dismissed')) {
        if (this._mode === 'shopify' && this._videoEl) {
          this._computeShopifyLoopRange();
          try {
            this._videoEl.muted = true;
            this._videoEl.currentTime = this._shopifyLoopLo;
          } catch (_) {}
          var pv = this._videoEl.play();
          if (pv && typeof pv.then === 'function') pv.catch(function () {});
        } else if (this._mode === 'youtube' && this._youtubeId) {
          this._loadIframePreviewYoutube();
        } else if (this._mode === 'vimeo' && this._vimeoId) {
          this._loadIframePreviewVimeo();
        }
      }

      var restore = this._prevFocus;
      this._prevFocus = null;
      if (restore && typeof restore.focus === 'function') {
        try {
          restore.focus();
        } catch (_) {}
      } else if (!this.classList.contains('is-dismissed') && this._btnPlay) {
        this._btnPlay.focus();
      }
    }

    _setShellDismissedA11y(hidden) {
      if (!this._shell) return;
      if (hidden) {
        this._shell.setAttribute('aria-hidden', 'true');
        if ('inert' in HTMLElement.prototype) this._shell.inert = true;
      } else {
        this._shell.removeAttribute('aria-hidden');
        if ('inert' in HTMLElement.prototype) this._shell.inert = false;
      }
    }

    _clearDismissAnimation() {
      if (this._dismissFallbackTimer) {
        clearTimeout(this._dismissFallbackTimer);
        this._dismissFallbackTimer = null;
      }
      if (this._shell && this._dismissTransitionHandler) {
        this._shell.removeEventListener('transitionend', this._dismissTransitionHandler);
        this._dismissTransitionHandler = null;
      }
    }

    _dismissCorner() {
      if (this.classList.contains('is-dismissed') || this.classList.contains('is-dismissing')) return;

      var self = this;
      var shell = this._shell;
      var reduceMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function finishDismiss() {
        self._clearDismissAnimation();
        self.classList.remove('is-dismissing');
        self.classList.add('is-dismissed');
        self._setShellDismissedA11y(true);
        self._stopIframePreviewTimers();
        self._pauseAll();
        if (self._mode === 'shopify' && self._videoEl) self._videoEl.controls = false;
        self._revealPoster(true);
        self._revealPlay(true);
      }

      if (reduceMotion || !shell) {
        finishDismiss();
        return;
      }

      var ended = false;
      function done() {
        if (ended) return;
        ended = true;
        finishDismiss();
      }

      this.classList.add('is-dismissing');

      this._dismissTransitionHandler = function (e) {
        if (e.target !== shell) return;
        if (e.propertyName !== 'opacity') return;
        done();
      };
      shell.addEventListener('transitionend', this._dismissTransitionHandler);
      this._dismissFallbackTimer = setTimeout(done, 480);
    }

    /**
     * 保证角标 shell 完整落在视口内；全屏、关闭后不处理。
     */
    _clampShellIntoViewport() {
      if (!this._shell) return;
      if (
        this.classList.contains('is-fullscreen') ||
        this.classList.contains('is-dismissed') ||
        this.classList.contains('is-dismissing')
      )
        return;

      var M = 8;
      var vp = readViewportSize();
      var vw = vp.vw;
      var vh = vp.vh;

      var shell = this._shell;
      var sr = shell.getBoundingClientRect();
      var sw = sr.width || shell.offsetWidth;
      var sh = sr.height || shell.offsetHeight;
      if (!(sw > 0 && sh > 0)) return;

      var cs = getComputedStyle(shell);
      var sl = parsePx(shell.style.left, NaN);
      var sb = parsePx(shell.style.bottom, NaN);
      if (!Number.isFinite(sl)) sl = parsePx(cs.left, 16);
      if (!Number.isFinite(sb)) sb = parsePx(cs.bottom, 16);

      var maxSL = vw - sw - M;
      var maxSB = vh - sh - M;
      var shiftX = 0;
      var shiftY = 0;

      if (maxSL >= M) {
        if (sl < M) shiftX = M - sl;
        else if (sl > maxSL) shiftX = maxSL - sl;
      } else {
        shiftX = M - sl;
      }

      if (maxSB >= M) {
        if (sb < M) shiftY = M - sb;
        else if (sb > maxSB) shiftY = maxSB - sb;
      } else {
        shiftY = M - sb;
      }

      if (shiftX === 0 && shiftY === 0) return;

      sl += shiftX;
      sb += shiftY;

      shell.style.left = sl + 'px';
      shell.style.bottom = sb + 'px';
      shell.style.right = 'auto';
      shell.style.top = 'auto';
    }

    _onDocKeydown(e) {
      if (e.key === 'Escape' && this.classList.contains('is-fullscreen')) {
        e.preventDefault();
        this._exitFullscreen();
      }
    }

    _onShellKeydown(e) {
      if (!this.classList.contains('is-fullscreen') || e.key !== 'Tab') return;

      var focusables = getFocusable(this._shell);
      if (!focusables.length) return;

      if (focusables.length === 1) {
        e.preventDefault();
        focusables[0].focus();
        return;
      }

      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !this._shell.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    _setupDrag(el) {
      var self = this;

      function ignoreDragTarget(node) {
        return !!(node && node.closest && node.closest('button'));
      }

      function onDown(clientX, clientY, target) {
        if (!self._shell) return;
        if (
          self.classList.contains('is-fullscreen') ||
          self.classList.contains('is-dismissed') ||
          self.classList.contains('is-dismissing')
        )
          return;
        if (ignoreDragTarget(target)) return;
        self._dragging = true;
        self._dragStartX = clientX;
        self._dragStartY = clientY;
        var ss = getComputedStyle(self._shell);
        self._dragShellOrigLeft = parsePx(ss.left, 16);
        self._dragShellOrigBottom = parsePx(ss.bottom, 16);
        self._shell.style.transition = 'none';
        window.addEventListener('mousemove', self._onPointerMove);
        window.addEventListener('mouseup', self._onPointerUp);
        window.addEventListener('touchmove', self._onPointerMove, { passive: true });
        window.addEventListener('touchend', self._onPointerUp);
      }

      el.addEventListener('mousedown', function (e) {
        onDown(e.clientX, e.clientY, e.target);
      });

      el.addEventListener(
        'touchstart',
        function (e) {
          if (!e.touches.length) return;
          onDown(e.touches[0].clientX, e.touches[0].clientY, e.target);
        },
        { passive: true }
      );
    }

    _onPointerMove(e) {
      if (!this._dragging || !this._shell) return;

      var clientX = e.touches && e.touches.length ? e.touches[0].clientX : e.clientX;
      var clientY = e.touches && e.touches.length ? e.touches[0].clientY : e.clientY;

      var dx = clientX - this._dragStartX;
      var dy = clientY - this._dragStartY;

      var M = 8;
      var vp = readViewportSize();
      var vw = vp.vw;
      var vh = vp.vh;

      var sr = this._shell.getBoundingClientRect();
      var sw = sr.width || this._shell.offsetWidth;
      var sh = sr.height || this._shell.offsetHeight;
      if (!(sw > 0 && sh > 0)) return;

      var newShellLeft = this._dragShellOrigLeft + dx;
      var newShellBottom = this._dragShellOrigBottom - dy;

      newShellLeft = clamp(newShellLeft, M, vw - sw - M);
      newShellBottom = clamp(newShellBottom, M, vh - sh - M);

      this._shell.style.left = newShellLeft + 'px';
      this._shell.style.bottom = newShellBottom + 'px';
      this._shell.style.right = 'auto';
      this._shell.style.top = 'auto';
    }

    _onPointerUp() {
      if (!this._dragging || !this._shell) return;
      this._dragging = false;
      this._shell.style.transition = '';
      this._dragShellOrigLeft = parsePx(this._shell.style.left, 16);
      this._dragShellOrigBottom = parsePx(this._shell.style.bottom, 16);
      window.removeEventListener('mousemove', this._onPointerMove);
      window.removeEventListener('mouseup', this._onPointerUp);
      window.removeEventListener('touchmove', this._onPointerMove);
      window.removeEventListener('touchend', this._onPointerUp);
    }
  }

  if (!customElements.get('c-video-popup')) {
    customElements.define('c-video-popup', CVideoPopup);
  }
})();
